import { CalendarIcon } from '@va/icons';
import { useLocalizationContext } from '@va/localization';
import { getLocalizedDate } from '@va/util/helpers';
import classNames from 'classnames';
import { Dayjs } from 'dayjs';
import { useState } from 'react';
import { IconWithBackground } from '../../icon-with-background';
import { DateInputContextProvider } from './DateInputContext';
import { DateTooltip } from './DateTooltip';
import './date-input.scss';
import { PickerConfig } from './types';

export type DateInputProps = {
  onChange?: (value: Dayjs) => void;
  value: Dayjs | undefined;
  name?: string;
  pickerConfig?: PickerConfig;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  label?: string;
  'data-testid'?: string;
};

export const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  pickerConfig,
  'data-testid': dataTestId,
  className,
  disabled,
  label,
  name,
  required,
}) => {
  const [open, setOpen] = useState(false);
  const { locale } = useLocalizationContext();

  return (
    <DateInputContextProvider pickerConfig={pickerConfig} onChange={onChange} value={value}>
      <DateTooltip onOpenChange={setOpen} open={open}>
        <div
          className={classNames(
            'relative flex items-center justify-between rounded-15 border overflow-hidden p-1.5 pl-18px gap-15px h-60px min-w-fit',
            {
              'bg-white-snow hover:bg-gray-concrete border-transparent': !open,
              'bg-white border-primary': open,
            },
            className,
          )}
        >
          <input
            data-testid={dataTestId}
            type='date'
            className={classNames(
              'date-input-field absolute left-0 top-0 opacity-0 h-full w-full bg-transparent font-medium outline-hidden leading-6 text-gray-charcoal text-15',
              {
                'transform-none! no-label': !label,
                'cursor-not-allowed': disabled,
                'with-label': label,
              },
            )}
            name={name}
            disabled={disabled}
          />
          <div className='relative h-full w-full flex items-end pb-1'>
            <div className='w-fit text-15 leading-6 font-medium'>{value && getLocalizedDate(value.unix(), locale)}</div>
            {label && (
              <label
                htmlFor={name}
                className={classNames(
                  'text-15 absolute top-1/2 -translate-y-1/2 left-0 font-primary whitespace-nowrap truncate block leading-none max-w-full font-medium pointer-events-none transition-transform text-gray-devil',
                  {
                    '-translate-y-3.75 scale-75 origin-top-left': !!value,
                  },
                )}
              >
                {label}
                {required && <span className='text-red-negative ml-1'>*</span>}
              </label>
            )}
          </div>
          <IconWithBackground
            type='square'
            icon={() => <CalendarIcon className='text-gray-charcoal' />}
            className={classNames('bg-white rounded-12 flex items-center shrink-0 justify-center', {
              'bg-white-snow': open,
            })}
          />
        </div>
      </DateTooltip>
    </DateInputContextProvider>
  );
};
